import {
  Badge,
  Card,
  CardHeader,
  Divider,
  makeStyles,
  Text,
  tokens,
} from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ClientRoutes } from "../../models/ClientRoutes";
import { Field } from "../../models/Field";
import { Tool } from "../../models/Tool";
import ToolHistoryDiffCard from "./ToolHistoryDiffCard";

const useStyles = makeStyles({
  metadata: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    paddingTop: tokens.spacingVerticalS,
  },
  metadataRow: {
    display: "flex",
    gap: tokens.spacingHorizontalL,
  },
  metadataLabel: {
    fontWeight: tokens.fontWeightSemibold,
    minWidth: "120px",
  },
  historyCard: {
    marginBottom: tokens.spacingVerticalL,
  },
  currentVersionLabel: {
    marginRight: tokens.spacingHorizontalS,
  },
});

interface ToolHistoryListProps {
  tools: Tool[];
  toolFields: Field[];
}

const ToolHistoryList = (props: ToolHistoryListProps) => {
  const { tools, toolFields } = props;
  const { t } = useTranslation();
  const styles = useStyles();

  return (
    <div>
      {tools.map((tool, index) => {
        const isCurrent = index === 0;
        const previousTool = tools[index + 1];
        const route = tool.isCurrentVersion
          ? `${ClientRoutes.ToolsPublished}/${tool.versionId}`
          : `${ClientRoutes.ToolsPublishedOld}/${tool.versionId}`;

        return (
          <Card key={tool.versionId} className={styles.historyCard}>
            <CardHeader
              header={
                <div>
                  {!isCurrent && <Link to={route}>{tool.title}</Link>}
                  {isCurrent && (
                    <>
                      <Text className={styles.currentVersionLabel}>
                        {tool.title}
                      </Text>
                      <Badge appearance="filled" color="informative">
                        {t("active")}
                      </Badge>
                    </>
                  )}
                </div>
              }
            />

            <Divider />

            <div className={styles.metadata}>
              <div className={styles.metadataRow}>
                <Text className={styles.metadataLabel}>{t("version")}:</Text>
                <Text>{tool.version}</Text>
              </div>

              {tool.comments && tool.comments[0]?.message && (
                <div className={styles.metadataRow}>
                  <Text className={styles.metadataLabel}>{t("comments")}:</Text>
                  <Text>{tool.comments[0].message}</Text>
                </div>
              )}

              <div className={styles.metadataRow}>
                <Text className={styles.metadataLabel}>
                  {t("publishedDate")}:
                </Text>
                <Text>
                  {tool.publishedTimeInUTC
                    ? new Date(tool.publishedTimeInUTC).toLocaleString()
                    : "-"}
                </Text>
              </div>

              <div className={styles.metadataRow}>
                <Text className={styles.metadataLabel}>
                  {t("publishedBy")}:
                </Text>
                <Text>{tool.publishedBy?.displayName || "-"}</Text>
              </div>
            </div>

            <div>
              <ToolHistoryDiffCard
                currentToolVersion={tool}
                previousTool={previousTool}
                toolFields={toolFields}
              />
            </div>
          </Card>
        );
      })}
    </div>
  );
};

export default ToolHistoryList;
